import type { SystemSchema } from './customData.interfaces';

export type SystemRelationSchemaName = 'products' | 'contacts';

export interface SystemRelationSchemaDefinition {
  name: SystemRelationSchemaName;
  label: string;
  relationFields: Array<{
    name: string;
    types: string[];
  }>;
  endpoint: string;
  idField: string;
  preferredPreviewFields: string[];
}

const toLabel = (value: string): string => value
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

export const SYSTEM_RELATION_SCHEMAS: SystemRelationSchemaDefinition[] = [
  {
    name: 'products',
    label: 'Products',
    relationFields: [
      { name: 'product_id', types: ['text', 'number'] },
    ],
    endpoint: '/products',
    idField: 'product_id',
    preferredPreviewFields: ['name', 'reference', 'price', 'updated_at'],
  },
  {
    // Le champ cible détermine la sémantique de la relation :
    // - id_contact : chaque record APPARTIENT à un contact (historique) ;
    // - group_id / lang / country : chaque record S'APPLIQUE à l'ensemble des
    //   contacts partageant cette valeur (id externe du groupe côté BO du
    //   marchand, code langue, code pays — tels que synchronisés). C'est ce
    //   qui permet ex. une grille tarifaire par groupe surchargeant les prix,
    //   résolue nativement par contact, sans table d'affectation à alimenter.
    name: 'contacts',
    label: 'Contacts',
    relationFields: [
      { name: 'id_contact', types: ['number'] },
      { name: 'group_id', types: ['text'] },
      { name: 'lang', types: ['text'] },
      { name: 'country', types: ['text'] },
    ],
    endpoint: '/contacts',
    idField: 'id_contact',
    preferredPreviewFields: ['contact_last_name', 'contact_first_name', 'contact_data_email', 'updated_at'],
  },
];

export const getSystemRelationSchemaDefinition = (
  schemaName: string,
): SystemRelationSchemaDefinition | undefined => SYSTEM_RELATION_SCHEMAS
  .find((schema) => schema.name === schemaName);

export const buildSystemSchemasForRelationEditor = (): SystemSchema[] => SYSTEM_RELATION_SCHEMAS
  .map((schema) => ({
    name: schema.name,
    label: schema.label || toLabel(schema.name),
    fields: schema.relationFields.flatMap((field) => field.types.map((type) => ({
      name: field.name,
      label: toLabel(field.name),
      type,
    }))),
  }));
